import { createUserWithEmailAndPassword, sendEmailVerification, type User } from "firebase/auth";
import { useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { AuthEmailError, requestVerificationEmail, usesServerEmail } from "@shared/auth-email";
import { useAuth } from "../../auth/AuthProvider";
import { Button } from "../../components/Button";
import { CheckboxField, FormAlert, SecretField, TextField, focusFirstInvalid } from "../../components/fields";
import { useDocumentTitle } from "../../components/layout";
import { patch } from "../../lib/api";
import { authErrorMessage } from "../../lib/firebase";
import type { PublicConfig } from "../../lib/types";
import { rememberNext, safeNext, withNext } from "../guards";
import { AuthLayout } from "./AuthLayout";
import { EMAIL_RE } from "./Login";

export function verificationSettings() {
  return { url: `${window.location.origin}/verify-email`, handleCodeInApp: false };
}

/**
 * Send the "confirm your email" message by whichever path this deployment uses: Supperkeep's own
 * branded email from the server, or — when the Firebase Auth emulator is in use locally — Firebase's
 * own flow, whose codes land in the dev outbox.
 */
export async function sendVerificationEmail(user: User, config: PublicConfig): Promise<void> {
  if (!usesServerEmail(config)) {
    await sendEmailVerification(user, verificationSettings());
    return;
  }
  // The server picks the recipient from this token; it is never sent as a request field.
  await requestVerificationEmail(await user.getIdToken());
}

/** Server messages are already product copy; Firebase errors get the app's own wording. */
export const verificationErrorMessage = (error: unknown): string =>
  error instanceof AuthEmailError ? error.message : authErrorMessage(error);

export function RegisterPage() {
  useDocumentTitle("Create account");
  const { auth, config, user, initializing } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const formRef = useRef<HTMLFormElement>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // The policies require both acknowledgements before an account exists, so they are checked here
  // and the Firebase account is never created unless both are ticked.
  const [adult, setAdult] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; name?: string; adult?: string; agreed?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!initializing && user && !busy) return <Navigate to={user.emailVerified ? safeNext(params.get("next")) : withNext("/verify-email", params.get("next"))} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const next: typeof errors = {};
    if (name.trim().length > 80) next.name = "Use 80 characters or fewer.";
    if (!email.trim()) next.email = "Enter your email address.";
    else if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email address, like sam@example.com.";
    if (password.length < 8) next.password = "Use at least 8 characters.";
    if (!adult) next.adult = "Supperkeep accounts are for adults 18 and older.";
    if (!agreed) next.agreed = "Agree to the terms and confirm you have read the privacy policy.";
    setErrors(next);
    setFormError(null);
    if (Object.keys(next).length) {
      focusFirstInvalid(formRef.current);
      return;
    }
    setBusy(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      if (params.get("next")) rememberNext(safeNext(params.get("next")));
      if (name.trim()) {
        try {
          await patch("/api/v1/me", { displayName: name.trim() });
        } catch {
          // The name can be set later in Settings; do not block sign-up.
        }
      }
      // The account exists either way. If the email could not be sent, say so on the next screen
      // rather than claiming it is on its way — there is a Resend button there.
      let sent = true;
      try {
        await sendVerificationEmail(cred.user, config);
      } catch {
        sent = false;
      }
      navigate(withNext(sent ? "/verify-email?sent=1" : "/verify-email?sent=0", params.get("next")), { replace: true });
    } catch (error) {
      setFormError(authErrorMessage(error));
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      lead="You'll confirm your email, then set up or join a household."
      footer={
        <p>
          Already have an account? <Link to={withNext("/login", params.get("next"))}>Sign in</Link>
        </p>
      }
    >
      <form ref={formRef} noValidate onSubmit={submit} className="stack">
        <FormAlert>{formError}</FormAlert>
        <TextField label="Your name" optional autoComplete="name" value={name} error={errors.name} onChange={(e) => setName(e.target.value)} hint="Shown to people in your household." />
        <TextField label="Email" type="email" autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} error={errors.email} onChange={(e) => setEmail(e.target.value)} required />
        <SecretField label="Password" autoComplete="new-password" value={password} error={errors.password} hint="At least 8 characters. A short phrase is easy to remember." onChange={(e) => setPassword(e.target.value)} required minLength={8} />
        <div className="auth-consent">
          <CheckboxField
            label="I am 18 or older."
            checked={adult}
            error={errors.adult}
            onChange={(e) => {
              setAdult(e.target.checked);
              if (e.target.checked) setErrors((prev) => ({ ...prev, adult: undefined }));
            }}
            required
          />
          <CheckboxField
            label={
              <>
                I agree to the <Link to="/terms" target="_blank" rel="noreferrer">Terms of use</Link> and have read the <Link to="/privacy" target="_blank" rel="noreferrer">Privacy policy</Link>.
              </>
            }
            checked={agreed}
            hint="The policy links open in a new tab."
            error={errors.agreed}
            onChange={(e) => {
              setAgreed(e.target.checked);
              if (e.target.checked) setErrors((prev) => ({ ...prev, agreed: undefined }));
            }}
            required
          />
        </div>
        <Button type="submit" emphasis="solid" intent="brand" size="lg" block busy={busy}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
